/** Marker the article client splits on to render the in-body module. */
export const SHOP_THE_ANSWER_MARKER = '<!--STA-->'

// Product-card anchors can contain entire blocks. Splitting before their
// closing tag makes the browser reconstruct the link around adjacent React
// elements, so they must be balanced just like section containers.
const BLOCK_TAGS = ['a', 'div', 'ul', 'ol', 'table', 'blockquote', 'figure', 'section', 'aside', 'details', 'li', 'dl', 'nav', 'article']

/** True when every block container opened inside `segment` is also closed inside it. */
function blockBalanced(segment: string): boolean {
  const lower = segment.toLowerCase()
  for (const tag of BLOCK_TAGS) {
    const opens = lower.match(new RegExp(`<${tag}(?=[\\s>/])`, 'g'))?.length ?? 0
    const closes = lower.match(new RegExp(`</${tag}\\s*>`, 'g'))?.length ?? 0
    if (opens !== closes) return false
  }
  return true
}

/**
 * Insert the STA marker right after the ANSWER PARAGRAPH: the first `</p>`
 * that follows the first `</h2>` (the lead sits above the first section; the
 * first section's first paragraph is where the article answers its title).
 * When the article has no `<h2>`, the first paragraph is the answer.
 *
 * Nesting guard: the client renders each split chunk through its own
 * dangerouslySetInnerHTML, so a marker inside an unclosed block container
 * would let the browser auto-close it early. A candidate `</p>` whose segment
 * (from the search start) leaves a block open is skipped; after four
 * candidates the marker falls back to right after the first `</h2>`, then to
 * the top of the body. Idempotent — a body that already carries the marker is
 * returned as-is.
 */
export function insertShopTheAnswerMarker(html: string): string {
  if (!html) return html
  if (html.includes(SHOP_THE_ANSWER_MARKER)) return html

  const h2Close = /<\/h2\s*>/i.exec(html)
  const searchFrom = h2Close ? h2Close.index + h2Close[0].length : 0

  const pClose = /<\/p\s*>/gi
  pClose.lastIndex = searchFrom
  for (let attempt = 0; attempt < 4; attempt++) {
    const m = pClose.exec(html)
    if (!m) break
    const at = m.index + m[0].length
    if (blockBalanced(html.slice(searchFrom, at))) {
      return html.slice(0, at) + SHOP_THE_ANSWER_MARKER + html.slice(at)
    }
  }
  if (h2Close) {
    return html.slice(0, searchFrom) + SHOP_THE_ANSWER_MARKER + html.slice(searchFrom)
  }
  return SHOP_THE_ANSWER_MARKER + html
}

/** Place the second Google button at a balanced block boundary near mid-body. */
export function insertPreferredSourceMiddleMarker(html: string): string {
  if (html.includes('<!--PSM-->')) return html
  const answerEnd = html.indexOf(SHOP_THE_ANSWER_MARKER) + SHOP_THE_ANSWER_MARKER.length
  const midpoint = (Math.max(0, answerEnd) + html.length) / 2
  const candidates = [...html.matchAll(/<\/(?:a|p|div|section|table|ul|ol|blockquote|figure)\s*>/gi)]
    .map(match => match.index! + match[0].length)
    .filter(at => at > answerEnd)
    .sort((a, b) => Math.abs(a - midpoint) - Math.abs(b - midpoint))
  const at = candidates.find(at => blockBalanced(html.slice(0, at))) ?? html.length
  return html.slice(0, at) + '<!--PSM-->' + html.slice(at)
}
